import { useEffect } from 'react'
import { useIntl } from 'react-intl'

import usePublishCart from '~/App/Cart/hooks/usePublishCart'
import type { ChatThreadEvent } from '~/App/ChatThreads/@types'
import { IncomingMessage } from '~/App/components/messages/IncomingMessage'
import { MessageAction, MessageActions } from '~/App/components/messages/MessageActions'
import MessageText from '~/App/components/messages/MessageText'
import useSaveSessionChatThreadEvent from '~/App/SessionChatThreadsProvider/hooks/useSaveSessionChatThreadEvent'
import { toast } from '~/DesignSystem/components/sonner'

type InteractiveEventProps = {
  data: ChatThreadEvent
}

function InteractiveEvent({ data }: InteractiveEventProps) {
  const intl = useIntl()
  const {
    mutate: saveMessage,
    isPending: savingMessage,
    error: saveMessageError,
  } = useSaveSessionChatThreadEvent()
  const {
    mutate: publishCart,
    isPending: publishingCart,
    error: publishCartError,
  } = usePublishCart()

  const handleAction = (button: (typeof buttons)[number]) => {
    if (button.id === 'keep_shopping') {
      saveMessage(button.title)
    }
    if (button.id === 'buy_now') {
      publishCart(undefined)
    }
  }

  useEffect(() => {
    if (saveMessageError || publishCartError) {
      toast.error(intl.formatMessage({ id: 'error.generic' }))
    }
  }, [saveMessageError, publishCartError])

  if (data.attributes.sub_type !== 'interactive') return null

  const buttons = data.attributes.additional_fields.buttons
  const disabled = savingMessage || publishingCart

  return (
    <IncomingMessage
      owner={data.attributes.author?.data?.attributes.display_name || undefined}
      time={data.attributes.timestamp * 1000}
      key={data.id}
    >
      <MessageText>{data.attributes.text_in_chatter_language || data.attributes.text}</MessageText>

      {!!buttons.length && (
        <MessageActions>
          {buttons.map((item, key) => (
            <MessageAction
              key={item.id}
              onClick={() => handleAction(item)}
              disabled={disabled}
              variant={key < buttons.length - 1 ? 'secondary' : undefined}
            >
              {item.title}
            </MessageAction>
          ))}
        </MessageActions>
      )}
    </IncomingMessage>
  )
}

export default InteractiveEvent
